import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {NameGeneratorSeparator} from '@domain/name-generator/enums/name-generator-separator.enum';
import {NameGenerator} from '@domain/name-generator/name-generator';
import {getProperty, getPropertyValue, PropertyValue} from '@domain/property-value';
import {PRODUCT_CONFIGURATIONS, ProductConfigurations} from '@infrastructure/http/product-configuration/product-configuration.interface';
import {BackendError, BaseComponent, DialogBuilderFactoryService, DialogType, EnumUtils, ObjectActionType, ToastHelperService, TranslateService} from '@vdw/angular-component-library';
import {finalize, forkJoin, takeUntil} from 'rxjs';

@Component({
  templateUrl: './product-configuration-settings.component.html'
})
export class ProductConfigurationSettingsComponent extends BaseComponent implements OnInit {
  public nameGenerationProperties: string[];
  public generateNameConfiguration: NameGenerator;
  public productConfigurationCustomSettingsForm: FormGroup<{
    namePlaceholderSeparator: FormControl<NameGeneratorSeparator>;
  }>;

  private listOfCustomSettings: PropertyValue[] = [];

  public constructor(
    @Inject(PRODUCT_CONFIGURATIONS) private readonly productConfigurations: ProductConfigurations,
    private readonly toastHelperService: ToastHelperService,
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly navigationHelperService: NavigationHelperService<PropertyValue[]>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.createFormFields();
    this.initialiseCustomSettings();
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.onDestroy();
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(RouteUtils.paths.texEdit.productConfiguration.absolutePath);
  }

  public saveCustomSettings(): void {
    this.saving = true;
    this.generateNameConfiguration.version = this.generateNameConfiguration.version ?? 1;
    this.generateNameConfiguration.separator = this.productConfigurationCustomSettingsForm.value.namePlaceholderSeparator;
    getProperty(this.listOfCustomSettings, 'generateNameConfiguration').propertyValue = JSON.stringify(this.generateNameConfiguration);

    this.productConfigurations
      .saveCustomSettings(this.listOfCustomSettings)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving()))
      .subscribe(() => {
        this.toastHelperService.showToastForObjectAction(ObjectActionType.SAVE, this.translate.instant('GENERAL.CUSTOM_SETTINGS.CUSTOM_SETTINGS').toLowerCase(), null);
        this.navigateBack();
      });
  }

  public getKeysOfNameGeneratorSeparator(): string[] {
    return EnumUtils.getEnumNames(NameGeneratorSeparator);
  }

  private createFormFields(): void {
    this.productConfigurationCustomSettingsForm = this.formBuilder.group({namePlaceholderSeparator: this.formBuilder.control(NameGeneratorSeparator.NO_SEPARATOR)});
  }

  private setFormFields(): void {
    this.generateNameConfiguration = JSON.parse(getPropertyValue(this.listOfCustomSettings, 'generateNameConfiguration'));
    this.productConfigurationCustomSettingsForm.controls.namePlaceholderSeparator.patchValue(this.generateNameConfiguration.separator ?? NameGeneratorSeparator.NO_SEPARATOR);
  }

  private initialiseCustomSettings(): void {
    forkJoin([this.productConfigurations.getListOfCustomSettings(), this.productConfigurations.getNameGenerationProperties()])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe({
        next: ([listOfCustomSettings, nameGenerationProperties]: [PropertyValue[], string[]]) => {
          this.listOfCustomSettings = listOfCustomSettings;
          this.nameGenerationProperties = nameGenerationProperties;
          this.setFormFields();
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(errorMessage.message)
      });
  }

  private showErrorDialogForBackendError(message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant('PRODUCT_CONFIGURATION.PRODUCT_CONFIGURATION', {
        object: this.translate.instant('PRODUCT_CONFIGURATION.PRODUCT_CONFIGURATION', {count: 1})
      }),
      messageText: message,
      type: DialogType.ERROR
    });
  }
}
